import React from 'react';
import { Account } from '@brenger/api-client';
import { getIdFromIri } from '@brenger/utils';
import { useQuery } from '@tanstack/react-query';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import { CacheKey } from '../../../types';
import { coreClient } from '../../../utils/request';
import { actions } from '../ducks';
import { isUserLoggedIn } from '../../User/ducks';

interface UseCustomerPrefill {
  isLoading: boolean;
}

/**
 * Hook that takes care of prefilling the correct info
 */

export const useCustomerPrefill = (): UseCustomerPrefill => {
  const user = useTypedSelector(state => state.user);
  const isLoggedIn = useTypedSelector(isUserLoggedIn);

  const userData = user.userData;
  const accountData = userData?.account as Account | undefined;
  const addressIri = accountData?.billing_address || accountData?.address;
  const addressId = getIdFromIri(addressIri);
  const dispatch = useDispatch();

  const address = useQuery({
    queryKey: [CacheKey.RETRIEVE_ADDRESS, addressId],
    queryFn: () => coreClient.addresses.retrieve({ id: addressId as string }),
    enabled: !!addressId,
  });

  /**
   * PREFILL CUSTOMER DETAILS
   */
  React.useEffect(() => {
    if (!isLoggedIn) return;
    const details = {
      first_name: userData?.first_name || '',
      last_name: userData?.last_name || '',
      email: userData?.email || '',
      phone: userData?.phone || '',
    };
    dispatch(actions.setContactDetails({ contact_type: 'customer', details }));
  }, [userData, user.loading]);

  /**
   * PREFILL COMPANY DETAILS
   */
  React.useEffect(() => {
    if (!address.data || !isLoggedIn) return;

    const account = userData?.account as Account;
    const accName = account?.name;
    const accCocNumber = account?.coc_number;
    const accVatNumber = account?.vat_number;
    dispatch(actions.setContactIsBusiness(true));
    dispatch(
      actions.setCompanyDetails({
        company_name: accName,
        coc_number: accCocNumber || '',
        vat_number: accVatNumber || '',
        address: {
          line1: address.data?.line1,
          postal_code: address.data?.postal_code,
          locality: address.data?.locality,
          country_code: address.data?.country_code,
        },
      })
    );
  }, [address.data]);

  return {
    isLoading: user.loading || address.isLoading,
  };
};
